import type {
  BurnRateDistributionStrategy,
  BurnRateGranularity,
  IPaginatedResponse,
  IProject,
  IProjectBurnRate,
  IProjectClientContact,
  IProjectHoliday,
  IProjectKeyAvailability,
  IProjectModule,
  IProjectNote,
  IProjectPrerequisiteStatus,
  IProjectStaffingMember,
  IResourceWorkloadResponse,
  ProjectPhase,
  ResourceWorkloadScope,
} from '@pulse/shared';

import { apiFetch } from '../../../lib/api-client';
import { extractErrorMessage } from '../../../lib/api-error.util';

const BASE = '/api/v1/projects';

// §1.4 — pre-creation availability check so the form can validate a user-typed Project Key
// before submitting.
export async function checkProjectKeyAvailability(key: string): Promise<IProjectKeyAvailability> {
  const res = await apiFetch(`${BASE}/check-key?key=${encodeURIComponent(key)}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to check Project Key availability'));
  return res.json() as Promise<IProjectKeyAvailability>;
}

// Phase-agnostic Project API client — both the Delivery and Pre-Sales feature pages share this
// (the "Create" action itself differs per phase; see delivery-project.api.ts).
//
// Full flat list — used by pickers (the presales-project link field on ProjectFormDialog/
// ProjectOverviewTab) and the Home dashboard's role-based view, all of which genuinely need the
// full array. The Projects list page itself uses fetchProjectsPaginated() below instead.
export async function fetchProjects(phase?: ProjectPhase): Promise<IProject[]> {
  const url = phase ? `${BASE}?phase=${phase}` : BASE;
  const res = await apiFetch(url);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch projects: ${res.status}`));
  return res.json() as Promise<IProject[]>;
}

export interface ProjectListFilters {
  phase?: ProjectPhase;
  search?: string;
  /** Exact Account name filter (matches ANY of the given names) — powers both the Accounts
   *  list's project-count click-through (`?account=<name>`) and the desktop "Account" filter
   *  pill, both of which need this applied server-side rather than only to the loaded page. */
  accountNames?: string[];
  /** Same "matches ANY of the given names" semantics as accountNames — Business Unit / Project
   *  Type / Project Lead / Project Manager filter pills. */
  businessUnitNames?: string[];
  projectTypeNames?: string[];
  projectLeadNames?: string[];
  projectManagerNames?: string[];
  /** Industry Segment filter — matches ANY of the given segment values (exact string match). */
  industrySegments?: string[];
  /** Status filter — matches ANY of the given status values (exact string match). */
  statuses?: string[];
  /** Priority filter — matches ANY of the given priority values (exact string match). */
  priorities?: string[];
  limit?: number;
  offset?: number;
}

// Server-side paginated list — used only by ProjectListPage (the Pre-Sales/Delivery list pages).
export async function fetchProjectsPaginated(filters: ProjectListFilters = {}): Promise<IPaginatedResponse<IProject>> {
  const params = new URLSearchParams();
  if (filters.phase) params.set('phase', filters.phase);
  if (filters.search) params.set('search', filters.search);
  for (const name of filters.accountNames ?? []) params.append('account', name);
  for (const name of filters.businessUnitNames ?? []) params.append('businessUnit', name);
  for (const name of filters.projectTypeNames ?? []) params.append('projectType', name);
  for (const name of filters.projectLeadNames ?? []) params.append('projectLead', name);
  for (const name of filters.projectManagerNames ?? []) params.append('projectManager', name);
  for (const seg of filters.industrySegments ?? []) params.append('industrySegment', seg);
  for (const s of filters.statuses ?? []) params.append('status', s);
  for (const p of filters.priorities ?? []) params.append('priority', p);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  if (filters.offset !== undefined) params.set('offset', String(filters.offset));

  const res = await apiFetch(`${BASE}/paginated?${params.toString()}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, `Failed to fetch projects: ${res.status}`));
  return res.json() as Promise<IPaginatedResponse<IProject>>;
}

// Carries the HTTP status so callers can tell a 403 (no access to this project) from a real load failure.
export class HttpStatusError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
  }
}

export async function fetchProject(id: string): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${id}`);
  if (!res.ok) throw new HttpStatusError(await extractErrorMessage(res, `Failed to fetch project: ${res.status}`), res.status);
  return res.json() as Promise<IProject>;
}

export type UpdateProjectInput = Partial<
  Pick<IProject, 'name' | 'accountId' | 'businessUnitId' | 'projectTypeId' | 'startDate' | 'projectLeadId' | 'endDate'>
> & {
  // `null` explicitly clears these fields; `undefined` leaves them unchanged. Every field below
  // is clearable in ProjectEditDialog/ProjectLeadershipDialog — either via a "None" dropdown
  // option (priority, fundingTypeId, presalesProjectId, industrySegment) or by emptying a
  // text/date field (the rest). Client Information is no longer part of this general update —
  // see fetchProjectClientContacts/createProjectClientContact/updateProjectClientContact below.
  priority?: IProject['priority'] | null;
  industrySegment?: IProject['industrySegment'] | null;
  description?: string | null;
  fundingTypeId?: string | null;
  targetDate?: string | null;
  documentFolderLink?: string | null;
  projectManagerId?: string | null;
  /** `null` explicitly clears the linked Pre-Sales project; `undefined` leaves it unchanged. */
  presalesProjectId?: string | null;
};

export async function updateProject(id: string, data: UpdateProjectInput): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update project'));
  return res.json() as Promise<IProject>;
}

export interface UpdateProjectFinanceInput {
  pocFundingStatusId?: string;
  productionFundingStatusId?: string;
  // `null` explicitly clears the amount; `undefined`/omitted leaves it unchanged — see
  // ProjectFinanceDialog.tsx's money() helper.
  targetRevenue?: { amountCents: number; currencyId: string } | null;
  targetProfitability?: { amountCents: number; currencyId: string } | null;
  awsMrr?: { amountCents: number; currencyId: string } | null;
  approvedBudget?: { amountCents: number; currencyId: string } | null;
}

export async function updateProjectFinance(id: string, data: UpdateProjectFinanceInput): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${id}/finance`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update finance fields'));
  return res.json() as Promise<IProject>;
}

// Delivery Projects exemption flow — Project-Lead-only, separate from updateProjectFinance
// above (different authorization boundary server-side, see UpdateProjectFundedFlagsDto).
export interface UpdateProjectFundedFlagsInput {
  isFunded?: boolean;
}

export async function updateProjectFundedFlags(id: string, data: UpdateProjectFundedFlagsInput): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${id}/funded-flags`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update funded status'));
  return res.json() as Promise<IProject>;
}

// Finance tab — "burn rate" is a computed view (nothing is stored server-side beyond staffing
// + cost-rate data); from/to default to the project's own staffing/date bounds when omitted.
export async function fetchProjectBurnRate(
  projectId: string,
  granularity: BurnRateGranularity,
  distributionStrategy: BurnRateDistributionStrategy,
  from?: string,
  to?: string,
): Promise<IProjectBurnRate> {
  const params = new URLSearchParams({ granularity, distributionStrategy });
  if (from) params.set('from', from);
  if (to) params.set('to', to);
  const res = await apiFetch(`${BASE}/${projectId}/finance/burn-rate?${params.toString()}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch burn rate'));
  return res.json() as Promise<IProjectBurnRate>;
}

// Resource Levelling — sets/clears a staffed user's per-project capacity override (null =
// inherit the user's global weeklyCapacityHours default).
export async function updateStaffingAllocation(
  projectId: string,
  userId: string,
  allocatedHoursPerWeek: number | null,
): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${projectId}/staffing/${userId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ allocatedHoursPerWeek }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update capacity override'));
  return res.json() as Promise<IProject>;
}

// ── Staffing (dated staffing records — Add/Destaff/History) ────────────────────
export interface AddStaffingInput {
  userId: string;
  startDate?: string;
  endDate?: string;
  allocatedHoursPerWeek?: number;
}

export async function addStaffing(projectId: string, data: AddStaffingInput): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${projectId}/staffing`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add staffing'));
  return res.json() as Promise<IProject>;
}

export async function destaffUser(projectId: string, userId: string): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${projectId}/staffing/${userId}/destaff`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to destaff'));
  return res.json() as Promise<IProject>;
}

export async function fetchStaffingHistory(projectId: string): Promise<IProjectStaffingMember[]> {
  const res = await apiFetch(`${BASE}/${projectId}/staffing/history`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch staffing history'));
  return res.json() as Promise<IProjectStaffingMember[]>;
}

// Edits a staffing record's Start/End Date — only accepted by the backend while the record
// hasn't started yet (a future Start Date). Omit endDate to clear it back to open-ended/ongoing.
export interface EditStaffingDatesInput {
  startDate: string;
  endDate?: string;
}

export async function editStaffingDates(projectId: string, staffingId: string, data: EditStaffingDatesInput): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${projectId}/staffing/${staffingId}/dates`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update staffing dates'));
  return res.json() as Promise<IProject>;
}

export async function transitionProject(id: string, toStep: string): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${id}/transition`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ toStep }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to move project to the new step'));
  return res.json() as Promise<IProject>;
}

// ── Notes (§6) ──────────────────────────────────────────────────────────────
export async function fetchProjectNotes(projectId: string): Promise<IProjectNote[]> {
  const res = await apiFetch(`${BASE}/${projectId}/notes`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch notes'));
  return res.json() as Promise<IProjectNote[]>;
}

export async function createProjectNote(projectId: string, body: string): Promise<IProjectNote> {
  const res = await apiFetch(`${BASE}/${projectId}/notes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add note'));
  return res.json() as Promise<IProjectNote>;
}

export async function updateProjectNote(projectId: string, noteId: string, body: string): Promise<IProjectNote> {
  const res = await apiFetch(`${BASE}/${projectId}/notes/${noteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ body }),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update note'));
  return res.json() as Promise<IProjectNote>;
}

// ── Pre-Requisites (§7) ───────────────────────────────────────────────────────
export async function fetchProjectPrerequisites(projectId: string): Promise<IProjectPrerequisiteStatus[]> {
  const res = await apiFetch(`${BASE}/${projectId}/prerequisites`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch pre-requisites'));
  return res.json() as Promise<IProjectPrerequisiteStatus[]>;
}

export async function updateProjectPrerequisite(
  projectId: string,
  prerequisiteId: string,
  data: { checked: boolean; note?: string | null },
): Promise<IProjectPrerequisiteStatus[]> {
  const res = await apiFetch(`${BASE}/${projectId}/prerequisites/${prerequisiteId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update pre-requisite'));
  return res.json() as Promise<IProjectPrerequisiteStatus[]>;
}

// ── Modules (§8) ───────────────────────────────────────────────────────────────
export async function fetchProjectModules(projectId: string): Promise<IProjectModule[]> {
  const res = await apiFetch(`${BASE}/${projectId}/modules`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch modules'));
  return res.json() as Promise<IProjectModule[]>;
}

export async function createProjectModule(
  projectId: string,
  data: { name: string; description?: string | null },
): Promise<IProjectModule> {
  const res = await apiFetch(`${BASE}/${projectId}/modules`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create module'));
  return res.json() as Promise<IProjectModule>;
}

export async function updateProjectModule(
  projectId: string,
  moduleId: string,
  data: { name?: string; description?: string | null },
): Promise<IProjectModule> {
  const res = await apiFetch(`${BASE}/${projectId}/modules/${moduleId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update module'));
  return res.json() as Promise<IProjectModule>;
}

export async function setProjectModuleActive(projectId: string, moduleId: string, active: boolean): Promise<IProjectModule> {
  const res = await apiFetch(`${BASE}/${projectId}/modules/${moduleId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update module status'));
  return res.json() as Promise<IProjectModule>;
}

// ── Client Contacts (Setup > Client Contacts) ────────────────────────────────────
export interface ProjectClientContactInput {
  clientName?: string;
  clientEmail?: string | null;
  clientPhone?: string | null;
  role?: string | null;
}

export async function fetchProjectClientContacts(projectId: string): Promise<IProjectClientContact[]> {
  const res = await apiFetch(`${BASE}/${projectId}/client-contacts`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch client contacts'));
  return res.json() as Promise<IProjectClientContact[]>;
}

export async function createProjectClientContact(
  projectId: string,
  data: ProjectClientContactInput,
): Promise<IProjectClientContact> {
  const res = await apiFetch(`${BASE}/${projectId}/client-contacts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to add client contact'));
  return res.json() as Promise<IProjectClientContact>;
}

export async function updateProjectClientContact(
  projectId: string,
  contactId: string,
  data: ProjectClientContactInput,
): Promise<IProjectClientContact> {
  const res = await apiFetch(`${BASE}/${projectId}/client-contacts/${contactId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update client contact'));
  return res.json() as Promise<IProjectClientContact>;
}

export async function setProjectClientContactActive(
  projectId: string,
  contactId: string,
  active: boolean,
): Promise<IProjectClientContact> {
  const res = await apiFetch(`${BASE}/${projectId}/client-contacts/${contactId}/${active ? 'activate' : 'deactivate'}`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update client contact status'));
  return res.json() as Promise<IProjectClientContact>;
}

// ── Holidays (Setup > Holidays) ──────────────────────────────────────────────────
export async function fetchProjectHolidays(projectId: string): Promise<IProjectHoliday[]> {
  const res = await apiFetch(`${BASE}/${projectId}/holidays`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch holidays'));
  return res.json() as Promise<IProjectHoliday[]>;
}

export async function createProjectHoliday(
  projectId: string,
  data: { date: string; name: string },
): Promise<IProjectHoliday> {
  const res = await apiFetch(`${BASE}/${projectId}/holidays`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to create holiday'));
  return res.json() as Promise<IProjectHoliday>;
}

export async function updateProjectHoliday(
  projectId: string,
  holidayId: string,
  data: { date?: string; name?: string },
): Promise<IProjectHoliday> {
  const res = await apiFetch(`${BASE}/${projectId}/holidays/${holidayId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update holiday'));
  return res.json() as Promise<IProjectHoliday>;
}

export async function setProjectHolidayActive(projectId: string, holidayId: string, active: boolean): Promise<IProjectHoliday> {
  const res = await apiFetch(`${BASE}/${projectId}/holidays/${holidayId}/${active ? 'activate' : 'deactivate'}`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to update holiday status'));
  return res.json() as Promise<IProjectHoliday>;
}

// ── Resource Levelling — actual work logs ────────────────────────────────────────
export async function fetchResourceWorkload(
  projectId: string,
  from: string,
  to: string,
  scope: ResourceWorkloadScope,
): Promise<IResourceWorkloadResponse> {
  const params = new URLSearchParams({ from, to, scope });
  const res = await apiFetch(`${BASE}/${projectId}/resource-levelling/workload?${params.toString()}`);
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to fetch resource work logs'));
  return res.json() as Promise<IResourceWorkloadResponse>;
}

// ── SoW Attachment (§5d) ────────────────────────────────────────────────────
export async function uploadSowAttachment(projectId: string, file: File): Promise<IProject> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await apiFetch(`${BASE}/${projectId}/sow-attachment`, { method: 'POST', body: formData });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to upload SoW attachment'));
  return res.json() as Promise<IProject>;
}

export async function removeSowAttachment(projectId: string): Promise<IProject> {
  const res = await apiFetch(`${BASE}/${projectId}/sow-attachment/deactivate`, { method: 'POST' });
  if (!res.ok) throw new Error(await extractErrorMessage(res, 'Failed to remove SoW attachment'));
  return res.json() as Promise<IProject>;
}

export function sowAttachmentDownloadUrl(projectId: string): string {
  return `${BASE}/${projectId}/sow-attachment/download`;
}
